import {
  type BlobMetadata,
  CreatePackageRequest,
  DocumentPackageType,
  JobType,
  Language,
  BuildingPackageType,
  BuildingPackageWorkflowStep,
  BuildingJSONData,
  LayoutType,
  BuildingPackage,
  PipelineName,
  ProcessorPreferenceKey,
  EmployerRole,
  RfpRegion,
  SiteDueDiligenceFacilityType,
  ServiceEntityType,
  VersionCompareRole,
  type StartPipelineRequest,
  type StartPipelineResponse,
} from '@consigli/types';

import { errorToast, successToast } from '@/components/toast';

import { type BidReviewBidEntry } from './use-unified-service-form';
import { PackageInputMode } from '../version-compare-section';

const PRIMARY_REFERENCE = 'primary_reference';
const BID_LEVELLING_REFERENCE = 'bid_levelling';

// import { TemplateMode } from '../site-due-diligence-fields';

// File upload function using blob upload
export const uploadFiles = async (
  files: File[],
  packageId: string,
  uploadBlob: (file: File, packageId: string) => Promise<unknown>,
): Promise<void> => {
  if (files.length === 0) return;
  try {
    for (const file of files) {
      await uploadBlob(file, packageId);
    }
  } catch (error) {
    throw error;
  }
};

// Mutation types
export type CreatePackageMutation = (payload: CreatePackageRequest) => {
  unwrap: () => Promise<unknown>;
};
export type CreateBuildingPackageMutation = (params: {
  projectId: number;
  payload: {
    name: string;
    type: BuildingPackageType;
    file_ids: string[];
    workflow_step?: BuildingPackageWorkflowStep;
  };
}) => { unwrap: () => Promise<BuildingPackage> };
export type CreateBuildingPackageBuildingJsonMutation = (params: {
  projectId: number;
  buildingPackageId: string;
  data: BuildingJSONData;
}) => { unwrap: () => Promise<unknown> };
export type CreateLayoutOptimizationMutation = (params: {
  projectId: number;
  payload: {
    name: string;
    type: LayoutType;
    file_ids: string[];
    language?: Language;
    data: {
      comments?: string;
      floorHeight?: number | null;
    };
  };
}) => { unwrap: () => Promise<unknown> };

export type UpdatePackageMutation = (params: {
  projectId: number;
  packageId: string;
  data: { data: Record<string, unknown> };
  isHidden: boolean;
}) => { unwrap: () => Promise<unknown> };

export type StartPipelineMutation = (payload: StartPipelineRequest) => {
  unwrap: () => Promise<StartPipelineResponse>;
};

// Form submission parameters
export interface FormSubmissionParams {
  serviceType: ServiceEntityType | '';
  name: string;
  documentPackageType?: DocumentPackageType;
  buildingPackageType?: BuildingPackageType;
  layoutOptimizationType?: LayoutType;
  language?: Language;
  inputType?: 'files' | 'json';
  comments?: string;
  floorHeight?: number | null;
  files: File[];
  selectedConflictTypes?: ProcessorPreferenceKey[];
  employerRole?: EmployerRole | null;
  // RFP/RFQ Review specific
  rfpRegion?: RfpRegion | null;
  // Site Due Diligence specific
  siteDueDiligenceFacilityType?: SiteDueDiligenceFacilityType | null;
  // templateMode?: TemplateMode;
  // customTemplateFile?: File | null;
  // selectedCustomer?: string | null;
  locationLat?: string;
  locationLon?: string;
  documentFiles?: File[];
  // gisFiles?: File[];
  // urls?: string[];
  // allowLLMForDocuments?: boolean;
  // Bid Review (OFFER) specific
  bidReviewBids?: BidReviewBidEntry[];
  bidReviewServices?: string[];
  bidReviewAutoFillLevelingSheet?: boolean;
  bidReviewLevelingSheetFiles?: File[];
  tenderPackageMode?: 'existing' | 'new';
  existingTenderPackageId?: string | null;
  tenderPackageName?: string;
  tenderFiles?: File[];
  // Version Compare specific
  versionComparePrimaryMode?: PackageInputMode;
  versionCompareSecondaryMode?: PackageInputMode;
  versionComparePrimaryPackageId?: string | null;
  versionCompareSecondaryPackageId?: string | null;
  versionComparePrimaryFiles?: File[];
  versionCompareSecondaryFiles?: File[];
  versionCompareCommentsMode?: string;
  versionCompareCommentRegisterFiles?: File[];
  projectId: number;
  resetForm: () => void;
  createPackage: CreatePackageMutation;
  createBuildingPackage: CreateBuildingPackageMutation;
  createBuildingPackageBuildingJson: CreateBuildingPackageBuildingJsonMutation;
  createLayoutOptimization: CreateLayoutOptimizationMutation;
  updatePackage?: UpdatePackageMutation;
  startPipeline?: StartPipelineMutation;
  uploadBlob: (
    file: File,
    packageId: string,
    onProgress?: (progress: unknown) => void,
    additionalMetadata?: BlobMetadata,
  ) => Promise<unknown>;
  uploadFile: (file: File, projectId: number) => Promise<unknown>;
}

export interface FormSubmissionResult {
  isValid: boolean;
}

/**
 * Function to submit a document package form.
 * @param params - The parameters for the document package.
 * @returns The result of the document package submission.
 */
const submitDocumentPackage = async (params: FormSubmissionParams): Promise<boolean> => {
  const {
    projectId,
    name,
    language,
    documentPackageType,
    files,
    selectedConflictTypes,
    employerRole,
    // RFP/RFQ Review specific
    rfpRegion,
    // Site Due Diligence specific
    siteDueDiligenceFacilityType,
    // templateMode,
    // customTemplateFile,
    // selectedCustomer,
    locationLat,
    locationLon,
    documentFiles,
    // gisFiles,
    // urls,
    // allowLLMForDocuments,
    // Bid Review (OFFER) specific
    tenderPackageMode,
    existingTenderPackageId,
    tenderFiles,
    // Version Compare specific
    versionComparePrimaryMode = PackageInputMode.SELECT_EXISTING,
    versionCompareSecondaryMode = PackageInputMode.SELECT_EXISTING,
    versionComparePrimaryPackageId,
    versionCompareSecondaryPackageId,
    versionComparePrimaryFiles,
    versionCompareSecondaryFiles,
    versionCompareCommentsMode,
    versionCompareCommentRegisterFiles,
    resetForm,
    createPackage,
    updatePackage,
    startPipeline,
    uploadBlob,
  } = params;

  const isSiteDueDiligence = documentPackageType === DocumentPackageType.SITE_DUE_DILIGENCE;
  const isOffer = documentPackageType === DocumentPackageType.OFFER;
  const isVersionCompare = documentPackageType === DocumentPackageType.VERSION_COMPARE;

  try {
    // Version Compare flow: create VERSION_COMPARE package, upload/select each version, start pipeline
    if (isVersionCompare && startPipeline) {
      const primaryIsUpload = versionComparePrimaryMode === PackageInputMode.UPLOAD_NEW;
      const secondaryIsUpload = versionCompareSecondaryMode === PackageInputMode.UPLOAD_NEW;

      // Validate each version has either a selected package or uploaded files
      if (!primaryIsUpload && !versionComparePrimaryPackageId) {
        errorToast({ message: 'Select a package or upload files for Version A' });
        return false;
      }
      if (primaryIsUpload && !versionComparePrimaryFiles?.length) {
        errorToast({ message: 'Upload at least one file for Version A' });
        return false;
      }
      if (!secondaryIsUpload && !versionCompareSecondaryPackageId) {
        errorToast({ message: 'Select a package or upload files for Version B' });
        return false;
      }
      if (secondaryIsUpload && !versionCompareSecondaryFiles?.length) {
        errorToast({ message: 'Upload at least one file for Version B' });
        return false;
      }
      if (
        !primaryIsUpload &&
        !secondaryIsUpload &&
        versionComparePrimaryPackageId &&
        versionComparePrimaryPackageId === versionCompareSecondaryPackageId
      ) {
        errorToast({ message: 'Version A and Version B cannot be the same package' });
        return false;
      }

      const compareResponse = await createPackage({
        projectId,
        name,
        language: language!,
        type: DocumentPackageType.VERSION_COMPARE,
        data: {
          ...(!primaryIsUpload && { primaryPackageId: versionComparePrimaryPackageId }),
          ...(!secondaryIsUpload && { secondaryPackageId: versionCompareSecondaryPackageId }),
          commentsMode: versionCompareCommentsMode,
        },
      }).unwrap();
      const comparePackageId = (compareResponse as { id: string }).id;

      // Upload newly provided files directly to the VC package, tagged with their role
      const uploadFilesWithRole = async (files: File[], role: VersionCompareRole) => {
        for (const file of files) {
          await uploadBlob(file, comparePackageId, undefined, { versionCompareRole: role });
        }
      };

      if (primaryIsUpload && versionComparePrimaryFiles?.length) {
        await uploadFilesWithRole(versionComparePrimaryFiles, VersionCompareRole.PRIMARY_VERSION);
      }
      if (secondaryIsUpload && versionCompareSecondaryFiles?.length) {
        await uploadFilesWithRole(
          versionCompareSecondaryFiles,
          VersionCompareRole.SECONDARY_VERSION,
        );
      }
      if (versionCompareCommentRegisterFiles && versionCompareCommentRegisterFiles.length > 0) {
        await uploadFilesWithRole(
          versionCompareCommentRegisterFiles,
          VersionCompareRole.COMMENT_VERSION,
        );
      }

      await startPipeline({
        projectId,
        packageId: comparePackageId,
        pipelineName: PipelineName.VERSION_COMPARE,
        jobType: JobType.DOCUMENT_PACKAGE,
        notify: true,
        metadata: {
          ...(!primaryIsUpload && { primaryPackageId: versionComparePrimaryPackageId }),
          ...(!secondaryIsUpload && { secondaryPackageId: versionCompareSecondaryPackageId }),
          versionCompareCommentsPackageId: comparePackageId,
          commentsMode: versionCompareCommentsMode,
        },
      }).unwrap();

      resetForm();
      successToast({ message: 'Version compare created and pipeline started successfully' });
      return true;
    }

    // Bid Review (OFFER) flow: create single OFFER package with data payload, start BID_REVIEW pipeline
    if (isOffer && startPipeline) {
      const {
        bidReviewBids: bids = [],
        bidReviewServices: services = [],
        bidReviewAutoFillLevelingSheet: autoFillLeveling = false,
        bidReviewLevelingSheetFiles: levelingFiles = [],
      } = params;

      if (services.length === 0) {
        errorToast({
          message: 'Select at least one analysis service (Risk / Insight / Gaps or Bid vs. Bid)',
        });
        return false;
      }

      const riskSelected = services.includes('RISK_INSIGHT_GAPS');
      const bidVsBidSelected = services.includes('BID_VS_BID');
      const gapsPackageActive = riskSelected || (bidVsBidSelected && !autoFillLeveling);
      const uploadNewTender = gapsPackageActive && tenderPackageMode === 'new';

      // A tender / RFP baseline is required whenever the tender section is shown:
      // either an existing package is selected, or new tender files are uploaded.
      if (gapsPackageActive) {
        if (uploadNewTender) {
          if (!tenderFiles || tenderFiles.length === 0) {
            errorToast({ message: 'Upload at least one tender document to compare against' });
            return false;
          }
        } else if (!existingTenderPackageId) {
          errorToast({ message: 'Select a tender package to compare against' });
          return false;
        }
      }

      const bidslist = bids.map((bid) => bid.providerName.trim());
      const resolvedExistingPackageId =
        gapsPackageActive && !uploadNewTender ? (existingTenderPackageId ?? '') : '';

      const primaryLanguage = (bids[0]?.language as Language) ?? language!;

      const offerResponse = await createPackage({
        projectId,
        name,
        language: primaryLanguage,
        type: DocumentPackageType.OFFER,
        selectedConflictTypes,
        employerRole,
        data: {
          existingPackageId: resolvedExistingPackageId,
          bidslist,
          otherBids: [],
          services,
        },
      }).unwrap();
      const offerPackageId = (offerResponse as { id: string }).id;

      for (const bid of bids) {
        const reference = bid.providerName.trim();
        for (const file of bid.files) {
          await uploadBlob(file, offerPackageId, undefined, { reference });
        }
      }

      if (uploadNewTender && tenderFiles && tenderFiles.length > 0) {
        for (const file of tenderFiles) {
          await uploadBlob(file, offerPackageId, undefined, {
            reference: PRIMARY_REFERENCE,
          });
        }
      }

      if (bidVsBidSelected && autoFillLeveling && levelingFiles.length > 0) {
        for (const file of levelingFiles) {
          await uploadBlob(file, offerPackageId, undefined, {
            reference: BID_LEVELLING_REFERENCE,
          });
        }
      }

      await startPipeline({
        projectId,
        packageId: offerPackageId,
        pipelineName: PipelineName.BID_REVIEW,
        jobType: JobType.DOCUMENT_PACKAGE,
        notify: true,
      }).unwrap();

      resetForm();
      successToast({ message: 'Bid review created and pipeline started successfully' });
      return true;
    }

    const isRfpRfqReview = documentPackageType === DocumentPackageType.RFP_RFQ_REVIEW;

    const response = await createPackage({
      projectId,
      name,
      language: language!,
      type: documentPackageType!,
      selectedConflictTypes,
      employerRole,
      ...(isRfpRfqReview && rfpRegion && { data: { region: rfpRegion } }),
    }).unwrap();

    const packageId = (response as { id: string }).id;

    // Handle Site Due Diligence specific logic
    if (isSiteDueDiligence && updatePackage) {
      // Build the package data with site due diligence metadata
      const packageData: Record<string, unknown> = {
        facilityType: siteDueDiligenceFacilityType,
        // templateMode: templateMode || 'default',
        // allowLLMForDocuments: allowLLMForDocuments || false,
      };

      // if (siteDueDiligenceFacilityType === SiteDueDiligenceFacilityType.DATA_CENTER && selectedCustomer) {
      //   packageData.selectedCustomer = selectedCustomer;
      // }

      // Add location if provided
      if (locationLat && locationLon) {
        const lat = parseFloat(locationLat);
        const lon = parseFloat(locationLon);
        if (!isNaN(lat) && !isNaN(lon)) {
          packageData.locationLat = lat;
          packageData.locationLon = lon;
        }
      }

      // if (urls && urls.length > 0) {
      //   packageData.urls = urls;
      // }

      // Update package with metadata
      await updatePackage({
        projectId,
        packageId,
        data: { data: packageData },
        isHidden: false,
      }).unwrap();

      // Upload document files
      if (documentFiles && documentFiles.length > 0) {
        await uploadFiles(documentFiles, packageId, uploadBlob);
      }

      // if (gisFiles && gisFiles.length > 0) {
      //   await uploadFiles(gisFiles, packageId, uploadBlob);
      // }

      // if (templateMode === 'custom' && customTemplateFile) {
      //   await uploadFiles([customTemplateFile], packageId, uploadBlob);
      // }
    } else {
      // Standard document package - upload files normally
      if (files.length > 0) {
        await uploadFiles(files, packageId, uploadBlob);
      }
    }

    resetForm();
    successToast({ message: 'Document package created successfully' });
    return true;
  } catch (error) {
    console.error('Failed to create document package:', error);

    // Handle different types of errors
    let errorMessage = 'Failed to create document package';

    if (error && typeof error === 'object' && 'status' in error) {
      const apiError = error as { status: string; data?: string; originalStatus?: number };

      if (apiError.status === 'PARSING_ERROR' && apiError.originalStatus === 500) {
        errorMessage = 'Server error occurred. Please try again later.';
      } else if (apiError.originalStatus === 500) {
        errorMessage = 'Internal server error. Please contact support if the issue persists.';
      } else if (apiError.originalStatus === 400) {
        errorMessage = 'Invalid request. Please check your input and try again.';
      } else if (apiError.originalStatus === 401) {
        errorMessage = 'Authentication failed. Please refresh the page and try again.';
      } else if (apiError.originalStatus === 403) {
        errorMessage = 'You do not have permission to perform this action.';
      }
    }

    errorToast({ message: errorMessage });
    return false;
  }
};

/**
 * Function to submit a building package form.
 */
const submitBuildingPackage = async (params: FormSubmissionParams): Promise<boolean> => {
  const {
    projectId,
    name,
    buildingPackageType,
    inputType,
    files,
    resetForm,
    createBuildingPackage,
    createBuildingPackageBuildingJson,
    uploadFile,
  } = params;

  try {
    const fileIds: string[] = [];
    let buildingJson: BuildingJSONData | undefined;

    // Handle file uploads
    if (inputType === 'files' && files.length > 0) {
      for (const file of files) {
        const uploadedFile = (await uploadFile(file, projectId)) as { id: string };
        fileIds.push(uploadedFile.id);
      }
    }

    // Handle JSON file
    if (inputType === 'json' && files.length > 0) {
      try {
        const file = files[0];
        const uploadedFile = (await uploadFile(file, projectId)) as { id: string };
        fileIds.push(uploadedFile.id);

        const text = await file.text();
        buildingJson = JSON.parse(text) as BuildingJSONData;
      } catch (error) {
        console.error('Error reading JSON file:', error);
        errorToast({ message: 'Invalid JSON file format' });
        return false;
      }
    }

    const payload = {
      name,
      type: buildingPackageType!,
      file_ids: fileIds,
      ...(buildingPackageType === BuildingPackageType.LOAD_CALCULATION && {
        workflow_step: BuildingPackageWorkflowStep.PROCESSING_LOAD_CALCULATION_STEP,
      }),
    };

    const response = await createBuildingPackage({
      projectId,
      payload,
    }).unwrap();

    // Create building JSON if provided
    if (buildingJson && response.id) {
      await createBuildingPackageBuildingJson({
        projectId,
        buildingPackageId: response.id,
        data: buildingJson,
      });
    }

    resetForm();
    successToast({ message: 'Building package created successfully' });
    return true;
  } catch (error) {
    console.error('Failed to create building package:', error);

    // Handle different types of errors
    let errorMessage = 'Failed to create building package';

    if (error && typeof error === 'object' && 'status' in error) {
      const apiError = error as { status: string; data?: string; originalStatus?: number };

      if (apiError.status === 'PARSING_ERROR' && apiError.originalStatus === 500) {
        errorMessage = 'Server error occurred. Please try again later.';
      } else if (apiError.originalStatus === 500) {
        errorMessage = 'Internal server error. Please contact support if the issue persists.';
      } else if (apiError.originalStatus === 400) {
        errorMessage = 'Invalid request. Please check your input and try again.';
      } else if (apiError.originalStatus === 401) {
        errorMessage = 'Authentication failed. Please refresh the page and try again.';
      } else if (apiError.originalStatus === 403) {
        errorMessage = 'You do not have permission to perform this action.';
      }
    }

    errorToast({ message: errorMessage });
    return false;
  }
};

/**
 * Function to submit a layout optimization form.
 * @param params - The parameters for the layout optimization.
 * @returns The result of the layout optimization submission.
 */
const submitLayoutOptimization = async (params: FormSubmissionParams): Promise<boolean> => {
  const {
    projectId,
    name,
    layoutOptimizationType,
    language,
    comments,
    floorHeight,
    files,
    resetForm,
    createLayoutOptimization,
    uploadFile,
  } = params;

  try {
    const fileIds: string[] = [];

    // Handle file uploads
    if (files.length > 0) {
      for (const file of files) {
        const uploadedFile = (await uploadFile(file, projectId)) as { id: string };
        fileIds.push(uploadedFile.id);
      }
    }

    const payload = {
      name,
      type: layoutOptimizationType!,
      file_ids: fileIds,
      ...(language && { language }),
      data: {
        comments: comments || undefined,
        floorHeight: floorHeight || undefined,
      },
    };

    await createLayoutOptimization({
      projectId,
      payload,
    }).unwrap();

    resetForm();
    successToast({ message: 'Layout optimization created successfully' });
    return true;
  } catch (error) {
    console.error('Failed to create layout optimization:', error);

    // Handle different types of errors
    let errorMessage = 'Failed to create layout optimization';

    if (error && typeof error === 'object' && 'status' in error) {
      const apiError = error as { status: string; data?: string; originalStatus?: number };

      if (apiError.status === 'PARSING_ERROR' && apiError.originalStatus === 500) {
        errorMessage = 'Server error occurred. Please try again later.';
      } else if (apiError.originalStatus === 500) {
        errorMessage = 'Internal server error. Please contact support if the issue persists.';
      } else if (apiError.originalStatus === 400) {
        errorMessage = 'Invalid request. Please check your input and try again.';
      } else if (apiError.originalStatus === 401) {
        errorMessage = 'Authentication failed. Please refresh the page and try again.';
      } else if (apiError.originalStatus === 403) {
        errorMessage = 'You do not have permission to perform this action.';
      }
    }

    errorToast({ message: errorMessage });
    return false;
  }
};

/**
 * Function to handle the form submission.
 * It bundles all the form submissions into a single function, to simplify the interface of the form.
 * Selects the appropriate form submission function based on the service type.
 * @param params - The parameters for the form submission.
 * @returns The result of the form submission.
 */
export const handleFormSubmit = async (
  params: FormSubmissionParams,
): Promise<FormSubmissionResult> => {
  const { serviceType } = params;

  // Submit based on service type
  let success = false;
  if (serviceType === ServiceEntityType.DOCUMENT_PACKAGE) {
    success = await submitDocumentPackage(params);
  } else if (serviceType === ServiceEntityType.BUILDING_PACKAGE) {
    success = await submitBuildingPackage(params);
  } else if (serviceType === ServiceEntityType.LAYOUT_OPTIMIZATION) {
    success = await submitLayoutOptimization(params);
  }

  return { isValid: success };
};
